import { useNavigate } from "@tanstack/react-router";
import { Laptop, Moon, Search, Sun } from "lucide-react";
import { useState } from "react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../../components/ui/command";
import { useCurrentUser, useHasPermission } from "../../features/auth/session";
import { useTheme } from "../theme/theme-context";
import { visibleNav } from "./nav-items";
import { useQuickSearch } from "./quick-search-context";

/** Ctrl+K: open any page, search for a card by what was typed, or change the theme. */
export function CommandMenu() {
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const canSearch = useHasPermission("search.run");
  const { setTheme } = useTheme();
  const { open, setOpen } = useQuickSearch();
  const [text, setText] = useState("");
  const words = text.trim();

  const run = (action: () => unknown) => {
    setOpen(false);
    setText("");
    action();
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setText("");
      }}
      title="Quick search"
      description="Open a page or search for a card"
    >
      <CommandInput placeholder="Type a page or a card name…" value={text} onValueChange={setText} />
      <CommandList>
        <CommandEmpty>Nothing found.</CommandEmpty>
        {canSearch && words.length >= 2 && (
          <CommandGroup heading="Cards">
            <CommandItem
              value={`search-card ${words}`}
              onSelect={() => run(() => navigate({ to: "/search", search: { q: words } }))}
            >
              <Search />
              <span>
                Search for “<span className="font-medium">{words}</span>”
              </span>
            </CommandItem>
          </CommandGroup>
        )}
        {visibleNav(permissions).map((group) => (
          <CommandGroup key={group.title} heading={group.title}>
            {group.items.map((item) => (
              <CommandItem
                key={item.to}
                value={item.title}
                keywords={[...(item.keywords ?? [])]}
                onSelect={() => run(() => navigate({ to: item.to }))}
              >
                <item.icon />
                <span>{item.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        <CommandSeparator />
        <CommandGroup heading="Theme">
          <CommandItem value="theme light" onSelect={() => run(() => setTheme("light"))}>
            <Sun />
            <span>Light</span>
          </CommandItem>
          <CommandItem value="theme dark" onSelect={() => run(() => setTheme("dark"))}>
            <Moon />
            <span>Dark</span>
          </CommandItem>
          <CommandItem value="theme system" onSelect={() => run(() => setTheme("system"))}>
            <Laptop />
            <span>Same as this computer</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
